import { useFormik } from "formik";
import { t } from "i18next";
import { DateTime } from "luxon";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabMenu } from "primereact/tabmenu";
import { Toolbar } from "primereact/toolbar";
import { classNames } from "primereact/utils";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useApis } from "../../../../api";
import PageLoader from "../../../../components/PageLoader";
import Panel from "../../../../components/Panel";
import Table, {
    TableHeader,
    TableSettings
} from "../../../../components/Table";
import { NotificationContext } from "../../../../context/NotificationContext";
import { ResourceContext } from "../../../../context/ResourcesContext";
import { Actions, Subjects, useAuth } from "../../../../hooks/useAuth";
import Layout from "../../../../layout/layout";
import { API_BASE_URL } from "../../../../utils/mocks";
import { EventStatus } from "../../index";
import "../index.css";

export enum SubscriptionTypes {
  onSite = "onSite",
  online = "online",
}

export enum SubscriptionPlatform {
  backOffice = "backOffice",
  form = "form",
}

export type Subscription = {
  id: string;
  event_id: string;
  student_id?: string;
  date: string;
  type: SubscriptionTypes;
  operator_id?: string;
  platform: SubscriptionPlatform;
  campusLocation?: string;
  degreeLevel?: string;
  course?: string;
  course_id?: string;
  courseStartYear?: number;
  courseStartMonth?: number;
  notes: string;
};

type Error = {
  date?: string;
  type?: string;
  studentName?: string;
  studentSurname?: string;
  studentEmail?: string;
  studentPrePhoneCode?: string;
  studentPhone?: string;
  studentCampusLocation?: string;
  studentDegreeLevel?: string;
  studentCourse?: string;
  studentStartingAt?: string;
  notes?: string;
  mailLanguage?: string;
};

export const types = [
  {
    label: t("app.subscriptions.types.online"),
    value: SubscriptionTypes.online,
  },
  {
    label: t("app.subscriptions.types.onSite"),
    value: SubscriptionTypes.onSite,
  },
];

export const platforms = [
  {
    label: t("app.subscriptions.platforms.backOffice"),
    value: SubscriptionPlatform.backOffice,
  },
  {
    label: t("app.subscriptions.platforms.form"),
    value: SubscriptionPlatform.form,
  },
];

const statuses = [
  {
    label: t("app.subscriptions.statuses.pending"),
    value: "pending",
  },
  {
    label: t("app.subscriptions.statuses.confirmed"),
    value: "confirmed",
  },
];

export default function SubscriptionComponent() {
  const {
    getEvent,
    getSubscriptions,
    createSubscription,
    deleteSubscription,
    getUserOperator,
    resendMailSubscription,
    getQrCodeSubscription,
    getBusinessUnits,
    getCurrentDegreeLevels,
    getCurrentCourses,
    getSubscription,
  } = useApis();
  const { id: event_id } = useParams<{ id: string }>();
  const { checkUserPermission, getAuthUser } = useAuth();
  const { countries, mailLanguages } = useContext(ResourceContext);
  const { addNotification } = useContext(NotificationContext);

  const qEvent = useQuery("event", () => getEvent(event_id!));

  const { data: eventData } = qEvent;

  const qCourses = useQuery("courses", getCurrentCourses);

  const { data: coursesData } = qCourses;

  const formik = useFormik({
    initialValues: {
      date: new Date().toISOString(),
      type: SubscriptionTypes.online,
      studentName: "",
      studentSurname: "",
      studentEmail: "",
      studentPrePhoneCode: "",
      studentPhone: "",
      studentCampusLocation: "",
      studentDegreeLevel: "",
      studentCourse: "",
      studentStartingAt: "",
      notes: "",
      mailLanguage: mailLanguages?.[0]?.value,
    },
    validate: (data) => {
      let errors: Error = {};

      if (!data.date) {
        errors.date = t("common.requiredField", {
          field: t("app.subscriptions.fields.date"),
        });
      }

      if (!data.type) {
        errors.type = t("common.requiredField", {
          field: t("app.subscriptions.fields.type"),
        });
      }

      if (!data.studentName) {
        errors.studentName = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentName"),
        });
      }

      if (data?.studentName?.length > 40) {
        errors.studentName = t("common.tooLongField", {
          field: t("app.subscriptions.fields.studentName"),
          max: 40,
        });
      }

      if (!data.studentSurname) {
        errors.studentSurname = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentSurname"),
        });
      }

      if (data.studentSurname?.length > 80) {
        errors.studentSurname = t("common.tooLongField", {
          field: t("app.subscriptions.fields.studentSurname"),
          max: 80,
        });
      }

      if (!data.studentEmail) {
        errors.studentEmail = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentEmail"),
        });
      }

      if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(data.studentEmail)
      ) {
        errors.studentEmail = t("common.invalidEmail");
      }

      if (data?.studentEmail?.length > 80) {
        errors.studentEmail = t("common.tooLongField", {
          field: t("app.subscriptions.fields.studentEmail"),
          max: 80,
        });
      }

      if (!data.studentPrePhoneCode) {
        errors.studentPrePhoneCode = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentPrePhoneCode"),
        });
      }

      if (!data.studentPhone) {
        errors.studentPhone = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentPhone"),
        });
      }

      if (data.studentPhone) {
        const numbers = countries.find(
          (el) => el.code === data.studentPrePhoneCode,
        )?.phoneCode;

        if (data?.studentPhone?.length > (numbers ? 16 - numbers.length : 15)) {
          errors.studentPhone = t("common.tooLongField", {
            field: t("app.subscriptions.fields.studentPhone"),
            max: 15,
          });
        }

        if (!/^\d+$/.test(data?.studentPhone)) {
          errors.studentPhone = t("common.invalidPhone");
        }
      }

      if (!data.studentCampusLocation) {
        errors.studentCampusLocation = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentCampusLocation"),
        });
      }

      if (!data.studentDegreeLevel) {
        errors.studentDegreeLevel = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentDegreeLevel"),
        });
      }

      if (!data.studentCourse) {
        errors.studentCourse = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentCourse"),
        });
      }

      if (!data.studentStartingAt) {
        errors.studentStartingAt = t("common.requiredField", {
          field: t("app.subscriptions.fields.studentStartingAt"),
        });
      }

      if (!data.mailLanguage) {
        errors.mailLanguage = t("common.requiredField", {
          field: t("app.subscriptions.fields.mailLanguage"),
        });
      }

      return errors;
    },
    onSubmit: async (data) => {
      const operator_id = (await qOperator.refetch())?.data?.data[0]?.id;
      const course = coursesData?.data?.find(
        (el: any) => el.id === data.studentStartingAt,
      );
      await mCreateSubscription.mutate({
        student: {
          name: data.studentName,
          surname: data.studentSurname,
          email: data.studentEmail,
          prePhoneCode: data.studentPrePhoneCode,
          phone: data.studentPhone,
          courseProductCode: course?.productCode,
          createdOn: "nabaOpenDayBO",
          marketingConsent: false,
          profilingConsent: false,
        },
        subscription: {
          event_id: event_id!,
          date: data.date,
          type: data.type,
          platform: SubscriptionPlatform.backOffice,
          campusLocation: data.studentCampusLocation,
          course_id: data.studentStartingAt,
          degreeLevel: data.studentDegreeLevel,
          course: data.studentCourse,
          courseStartYear: course?.startYear,
          courseStartMonth: course?.startMonth,
          notes: data.notes,
          operator_id,
          mailLang: data.mailLanguage,
        },
      });
    },
  });

  const [visibleSidebar, setVisibleSidebar] = useState(false);
  const [isSubscriptionFetching, setIsSubscriptionFetching] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [tableSettings, setTableSettings] = useState<TableSettings>({});
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>("");

  useQuery(
    ["subscription", selectedSubscriptionId, isSubscriptionFetching],
    () => getSubscription(selectedSubscriptionId),
    {
      enabled: !!selectedSubscriptionId && isSubscriptionFetching,
      onSuccess: (data) => {
        if (isSubscriptionFetching) {
          const {
            subscription: {
              date,
              type,
              campusLocation,
              degreeLevel,
              course,
              course_id,
              notes,
              mailLang,
              student: { name, surname, email, prePhoneCode, phone },
            },
          } = data;
          formik.setValues({
            studentName: name,
            studentSurname: surname,
            studentEmail: email,
            studentPrePhoneCode: prePhoneCode,
            studentPhone: phone,
            studentCampusLocation: campusLocation,
            studentDegreeLevel: degreeLevel,
            studentCourse: course,
            studentStartingAt: course_id,
            mailLanguage: mailLang,
            date,
            type,
            notes,
          });
          setIsSubscriptionFetching(false);
        }
      },
    },
  );

  const qOperator = useQuery(
    "operator",
    () => getUserOperator(getAuthUser().id, event_id!),
    {
      enabled: false,
    },
  );

  const qSubscriptions = useQuery(
    [
      "subscriptions",
      { ...tableSettings, additional: { "filter[event_id]": event_id! } },
    ],
    getSubscriptions,
    {
      onSuccess: (res: any) => {
        if (res.totalRecords) {
          setTotalRecords(parseInt(res.totalRecords));
        }
      },
      onSettled: () => {
        if (isFirstLoad) {
          setIsFirstLoad(false);
        }
      },
    },
  );

  const { isFetching, data } = qSubscriptions;

  const mCreateSubscription = useMutation(createSubscription, {
    onSuccess: () => {
      qSubscriptions.refetch();
      addNotification({
        title: t("common.creationSuccess"),
        type: "success",
      });
    },
    onError: (e: any) => {
      if (e?.message === "subscription_already_exists") {
        addNotification({
          title: t("errors.subscription_already_exists"),
          type: "error",
        });
      } else {
        addNotification({
          title: t("common.genericError"),
          type: "error",
        });
      }
    },
    onSettled: () => {
      setVisibleSidebar(false);
      formik.resetForm();
    },
  });

  const mDeleteSubscription = useMutation(deleteSubscription, {
    onSuccess: () => {
      qSubscriptions.refetch();
      addNotification({
        title: t("common.deleteSuccess"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
  });

  const mResendMailSubscription = useMutation(resendMailSubscription, {
    onSuccess: () => {
      addNotification({
        title: t("common.mailSent"),
        type: "success",
      });
    },
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
  });

  const mGetQrCodeSubscription = useMutation(getQrCodeSubscription, {
    onError: () => {
      addNotification({
        title: t("common.genericError"),
        type: "error",
      });
    },
  });

  const tableItems: Subscription[] = useMemo(() => {
    const subscriptions = data?.data || [];
    return subscriptions.map((subscription: any) => ({
      id: subscription?.id,
      name: subscription?.student?.name + " " + subscription?.student?.surname,
      email: subscription?.student?.email,
      date: new Date(subscription?.date).toLocaleDateString(),
      status: statuses.find((status) => status.value === subscription?.status)
        ?.label,
      type:
        types.find((type) => type.value === subscription?.type)?.label +
        " " +
        platforms.find((platform) => platform.value === subscription?.platform)
          ?.label,
    }));
  }, [data?.data]);

  const fields: TableHeader[] = useMemo(
    () => [
      {
        field: "name",
        header: t("app.subscriptions.fields.name"),
      },
      {
        field: "email",
        header: t("app.subscriptions.fields.email"),
      },
      {
        field: "type",
        header: t("app.subscriptions.fields.type"),
        sortable: true,
      },
      {
        field: "status",
        header: t("app.subscriptions.fields.status"),
        sortable: true,
      },
      {
        field: "date",
        header: t("app.subscriptions.fields.date"),
      },
    ],
    [],
  );

  const studentErrors = useMemo(() => {
    const keys = [
      "studentName",
      "studentSurname",
      "studentEmail",
      "studentPrePhoneCode",
      "studentPhone",
      "studentCampusLocation",
      "studentDegreeLevel",
      "studentCourse",
      "studentStartingAt",
    ];

    return keys.filter(
      // @ts-ignore
      (key) => formik.touched[key] && formik.errors[key],
    ).length;
  }, [formik]);

  const subscriptionErrors = useMemo(() => {
    const keys = ["date", "type", "notes"];

    return keys.filter(
      // @ts-ignore
      (key) => formik.touched[key] && formik.errors[key],
    ).length;
  }, [formik]);

  const tabs = [
    {
      id: "student",
      label: t("entities.student", { count: 1 }),
      template: ({ label }: any) => (
        <a className="p-menuitem-link" onClick={() => setActiveIndex(0)}>
          <span
            className="p-menuitem-text p-overlay-badge"
            data-pc-section="label"
          >
            {label}
            {studentErrors > 0 && (
              <Badge value={studentErrors} severity="danger"></Badge>
            )}
          </span>
        </a>
      ),
    },
    {
      id: "subscriptions",
      label: t("entities.subscription", { count: 1 }),
      template: ({ label }: any) => (
        <a
          href="#"
          className="p-menuitem-link"
          onClick={() => setActiveIndex(1)}
        >
          <span
            className="p-menuitem-text p-overlay-badge"
            data-pc-section="label"
          >
            {label}
            {subscriptionErrors > 0 && (
              <Badge value={subscriptionErrors} severity="danger"></Badge>
            )}
          </span>
        </a>
      ),
    },
  ];

  useEffect(() => {
    setTableSettings({
      sorting: {
        field: "type",
        order: -1,
      },
      pagination: {
        first: 0,
        rows: 10,
      },
    });
  }, [fields]);

  const isFormFieldValid = (name: keyof Error) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: keyof Error) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  const rightToolbarTemplate = () => {
    if (
      checkUserPermission(Subjects.subscriptions, Actions.create) &&
      eventData?.event?.status !== EventStatus.closed
    ) {
      return (
        <React.Fragment>
          <div className="my-2">
            <Button
              label={t("common.new")}
              icon="pi pi-plus"
              onClick={() => {
                formik.resetForm();
                setVisibleSidebar(true);
              }}
            />
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  };

  const getRowActions = () => {
    const rowActions: any = {};

    rowActions["onItemMail"] = {
      command: (item: Subscription) => {
        mResendMailSubscription.mutate(item.id);
      },
    };

    rowActions["onItemQR"] = {
      command: (item: Subscription) => {
        window.open(API_BASE_URL + `/api/subscriptions/${item.id}/qrCode/pdf`);
      },
    };

    // if (checkUserPermission(Subjects.subscriptions, Actions.delete)) {
    //   rowActions["onItemDelete"] = {
    //     command: (item: Subscription) => {
    //       mDeleteSubscription.mutate(item.id);
    //     },
    //   };
    // }

    return rowActions;
  };

  const qBusinessUnits = useQuery("businessUnits", getBusinessUnits);

  const { data: businessUnitsData } = qBusinessUnits;

  const qDegreeLevels = useQuery("degreeLevels", getCurrentDegreeLevels);

  const { data: degreeLevelsData } = qDegreeLevels;

  const businessUnitsOptions = useMemo(() => {
    const allBusinessUnits = businessUnitsData?.data || [];

    return allBusinessUnits.map(
      (place: any) =>
        ({
          label: place?.name,
          value: place.id.toString(),
        }) as any,
    );
  }, [businessUnitsData]);

  const degreeLevelOptions = useMemo(() => {
    const allDegreeLevels = degreeLevelsData?.data || [];

    const selectedBusinessUnitCode = businessUnitsData?.data
      ?.find(
        (el: any) => el.id.toString() === formik.values.studentCampusLocation,
      )
      ?.businessUnitId?.toString();

    return allDegreeLevels
      ?.filter(
        (dl: any) =>
          dl?.courses?.filter(
            (course: any) =>
              !!course.openDayVisible &&
              course.businessUnitCode === selectedBusinessUnitCode,
          )?.length > 0,
      )
      ?.map(
        (degreeLevel: any) =>
          ({
            label: degreeLevel.name,
            value: degreeLevel.id.toString(),
          }) as any,
      );
  }, [
    businessUnitsData?.data,
    degreeLevelsData?.data,
    formik.values.studentCampusLocation,
  ]);

  const courseCurriculumCodes = useMemo(() => {
    const selectedDegreeLevel = degreeLevelsData?.data?.find(
      (el: any) => el.id.toString() === formik.values.studentDegreeLevel,
    );

    const selectedBusinessUnitCode = businessUnitsData?.data
      ?.find(
        (el: any) => el.id.toString() === formik.values.studentCampusLocation,
      )
      ?.businessUnitId?.toString();

    const allCourses = selectedDegreeLevel?.courses?.filter(
      (course: any) =>
        !!course.openDayVisible &&
        course.businessUnitCode === selectedBusinessUnitCode,
    );

    const sortDataAlphabetically = (data: any) => {
      if (!data) return [];
      const sortedData = [...data].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      // Puoi utilizzare sortedData nel tuo componente
      return sortedData;
    };

    const uniqueCurriculumCodes = sortDataAlphabetically(allCourses).reduce(
      (acc: any, course: any) => {
        if (
          !acc.find((el: any) => el.curriculumCode === course.curriculumCode)
        ) {
          acc.push(course);
        }
        return acc;
      },
      [],
    );

    return (
      uniqueCurriculumCodes?.map((course: any) => ({
        label: course?.name + " - " + course?.language,
        value: course.curriculumCode,
      })) || []
    );
  }, [
    degreeLevelsData?.data,
    businessUnitsData?.data,
    formik.values.studentDegreeLevel,
    formik.values.studentCampusLocation,
  ]);

  const startingAtOptions = useMemo(() => {
    const selectedDegreeLevel = degreeLevelsData?.data?.find(
      (el: any) => el.id.toString() === formik.values.studentDegreeLevel,
    );

    const sortDataAlphabetically = (data: any) => {
      if (!data) return [];
      const sortedData = [...data].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      // Puoi utilizzare sortedData nel tuo componente
      return sortedData;
    };

    return sortDataAlphabetically(selectedDegreeLevel?.courses)
      .filter(
        (course: any) =>
          course.curriculumCode === formik.values.studentCourse &&
          !!course.openDayVisible,
      )
      ?.map((course: any) => ({
        value: course.id,
        label: `${DateTime.fromJSDate(new Date())
          .set({
            month: course.startMonth,
          })
          .toFormat("MMMM")} ${course.calendarYearReference}`,
      }));
  }, [
    formik.values.studentCourse,
    degreeLevelsData?.data,
    formik.values.studentDegreeLevel,
  ]);

  const selectedCountryTemplate = (option: any) => {
    if (!!option) {
      return (
        <div className="flex align-items-center">
          <img
            alt={option?.phoneCode}
            src="https://primefaces.org/cdn/primereact/images/flag/flag_placeholder.png"
            className={`mr-2 flag flag-${option?.code.toLowerCase()}`}
            style={{ width: "18px" }}
          />
          <div>{option?.phoneCode}</div>
        </div>
      );
    } else {
      return (
        <div className="flex align-items-center">
          <img
            alt={countries?.[0]?.phoneCode}
            src="https://primefaces.org/cdn/primereact/images/flag/flag_placeholder.png"
            className={`mr-2 flag flag-${countries?.[0]?.code.toLowerCase()}`}
            style={{ width: "18px" }}
          />
          <div>{countries?.[0]?.phoneCode}</div>
        </div>
      );
    }
  };

  const countryOptionTemplate = (option: any) => {
    return (
      <div className="flex align-items-center">
        <img
          alt={option?.name}
          src="https://primefaces.org/cdn/primereact/images/flag/flag_placeholder.png"
          className={`mr-2 flag flag-${option?.code.toLowerCase()}`}
          style={{ width: "18px" }}
        />
        <div>
          {option?.label} {option?.name}
        </div>
      </div>
    );
  };

  const orderedCountriesNumber = useMemo(() => {
    const italy = countries.find((el) => el.code === "IT");
    return [
      italy,
      ...countries.filter((el) => el.code !== "IT" && !!el.phoneCode),
    ];
  }, [countries]);

  return (
    <Layout>
      {isFirstLoad ? (
        <PageLoader />
      ) : (
        <div className="grid crud-demo">
          <Dialog
            visible={mGetQrCodeSubscription.data?.qrCode}
            onHide={() => mGetQrCodeSubscription.reset()}
          >
            {mGetQrCodeSubscription.data?.qrCode && (
              <img
                className="w-full"
                alt="qr-code"
                src={`data:image/png;base64,${mGetQrCodeSubscription.data?.qrCode}`}
              />
            )}
          </Dialog>
          <div className="col-12">
            <div className="card">
              <Toolbar
                className="border-0 mb-4"
                style={{ backgroundColor: "transparent" }}
                right={rightToolbarTemplate}
                left={() => (
                  <span className="text-2xl font-semibold">
                    {t("app.subscriptions.title")}
                  </span>
                )}
              ></Toolbar>
              <Table
                items={tableItems}
                fields={fields}
                rowActions={getRowActions()}
                loading={
                  isFetching ||
                  mResendMailSubscription.isLoading ||
                  mGetQrCodeSubscription.isLoading
                }
                settings={tableSettings}
                setSettings={setTableSettings}
                totalRecords={totalRecords}
                pagination
                onRowClick={(item: any) => {
                  setSelectedSubscriptionId(item.data.id);
                  setVisibleSidebar(true);
                  setIsSubscriptionFetching(true);
                }}
                selectionMode={true}
              />
              <Panel
                visible={visibleSidebar}
                setVisible={setVisibleSidebar}
                loading={
                  isSubscriptionFetching || mCreateSubscription.isLoading
                }
                title={t("common.newEntity", {
                  entity: t("entities.subscription", { count: 1 }),
                  context: "female",
                })}
                onHide={() => {
                  setTimeout(() => {
                    formik.resetForm();
                    setSelectedSubscriptionId("");
                    setActiveIndex(0);
                  }, 500);
                }}
                onSave={() => formik.handleSubmit()}
                actions={selectedSubscriptionId ? [] : undefined}
              >
                <form>
                  <TabMenu
                    key={activeIndex}
                    activeIndex={activeIndex}
                    model={tabs}
                  />
                  {activeIndex === 0 && !selectedSubscriptionId && (
                    <ul className="list-none m-0 p-0">
                      <li className="mt-3">
                        <label
                          htmlFor="studentName"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentName"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentName")}
                        </label>
                        <InputText
                          id="studentName"
                          name="studentName"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentName"),
                          })}
                          value={formik.values.studentName}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("studentName")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentSurname"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentSurname"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentSurname")}
                        </label>
                        <InputText
                          id="studentSurname"
                          name="studentSurname"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentSurname"),
                          })}
                          value={formik.values.studentSurname}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("studentSurname")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentEmail"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentEmail"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentEmail")}
                        </label>
                        <InputText
                          id="studentEmail"
                          name="studentEmail"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentEmail"),
                          })}
                          value={formik.values.studentEmail}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("studentEmail")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentPhone"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentPhone"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentPhone")}
                        </label>
                        <div className="p-inputgroup">
                          <span className="p-inputgroup-addon p-0 border-none">
                            <Dropdown
                              id="studentPrePhoneCode"
                              name="studentPrePhoneCode"
                              value={formik.values.studentPrePhoneCode}
                              onChange={formik.handleChange}
                              options={orderedCountriesNumber}
                              optionLabel="name"
                              optionValue="code"
                              valueTemplate={selectedCountryTemplate}
                              itemTemplate={countryOptionTemplate}
                              // panelFooterTemplate={panelFooterTemplate}
                              className="field-prePhone"
                              filter
                              filterBy="name"
                              showFilterClear
                            />
                          </span>
                          <InputText
                            id="studentPhone"
                            name="studentPhone"
                            className={classNames({
                              "field-phone-input": true,
                              "p-invalid": isFormFieldValid("studentPhone"),
                            })}
                            value={formik.values.studentPhone}
                            onChange={formik.handleChange}
                          />
                        </div>

                        {getFormErrorMessage("studentPhone")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentCampusLocation"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid(
                              "studentCampusLocation",
                            ),
                          })}
                        >
                          {t("app.subscriptions.fields.studentCampusLocation")}
                        </label>
                        <Dropdown
                          inputId="studentCampusLocation"
                          name="studentCampusLocation"
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid(
                              "studentCampusLocation",
                            ),
                          })}
                          options={businessUnitsOptions}
                          value={formik.values.studentCampusLocation}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                            formik.setFieldValue("studentDegreeLevel", "");
                            formik.setFieldValue("studentCourse", "");
                          }}
                        />
                        {getFormErrorMessage("studentCampusLocation")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentDegreeLevel"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentDegreeLevel"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentDegreeLevel")}
                        </label>
                        <Dropdown
                          inputId="studentDegreeLevel"
                          name="studentDegreeLevel"
                          optionLabel="label"
                          optionValue="value"
                          // optionGroupLabel="label"
                          // optionGroupChildren="items"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentDegreeLevel"),
                          })}
                          options={degreeLevelOptions}
                          disabled={!formik.values.studentCampusLocation}
                          value={formik.values.studentDegreeLevel}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                          }}
                        />
                        {getFormErrorMessage("studentDegreeLevel")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentCourse"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentCourse"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentCourse")}
                        </label>
                        <Dropdown
                          inputId="studentCourse"
                          name="studentCourse"
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentCourse"),
                          })}
                          options={courseCurriculumCodes}
                          disabled={!formik.values.studentCampusLocation}
                          value={formik.values.studentCourse}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                          }}
                          filter
                        />
                        {getFormErrorMessage("studentCourse")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="studentStartingAt"
                          className={classNames({
                            "font-bold block mb-2": true,
                            "p-error": isFormFieldValid("studentStartingAt"),
                          })}
                        >
                          {t("app.subscriptions.fields.studentStartingAt")}
                        </label>
                        <Dropdown
                          inputId="studentStartingAt"
                          name="studentStartingAt"
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("studentStartingAt"),
                          })}
                          options={startingAtOptions}
                          disabled={!formik.values.studentCampusLocation}
                          value={formik.values.studentStartingAt}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                          }}
                        />
                        {getFormErrorMessage("studentStartingAt")}
                      </li>
                    </ul>
                  )}
                  {activeIndex === 1 && !selectedSubscriptionId && (
                    <ul className="list-none m-0 p-0">
                      <li className="mt-3">
                        <label
                          htmlFor="date"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("date"),
                          })}
                        >
                          {t("app.subscriptions.fields.date")}
                        </label>
                        <Calendar
                          inputId="date"
                          name="date"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("date"),
                          })}
                          value={new Date(formik.values.date)}
                          onChange={formik.handleChange}
                          showIcon
                        />
                        {getFormErrorMessage("date")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="type"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("type"),
                          })}
                        >
                          {t("app.subscriptions.fields.type")}
                        </label>
                        <Dropdown
                          inputId="type"
                          name="type"
                          value={formik.values.type}
                          options={types}
                          optionLabel="label"
                          optionValue="value"
                          disabled={true}
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("type"),
                          })}
                          onChange={(e) => {
                            formik.setFieldValue("type", e.value);
                          }}
                        />
                        {getFormErrorMessage("type")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="mailLanguage"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("mailLanguage"),
                          })}
                        >
                          {t("app.subscriptions.fields.mailLanguage")}
                        </label>
                        <Dropdown
                          inputId="mailLanguage"
                          name="mailLanguage"
                          value={formik.values.mailLanguage}
                          options={mailLanguages}
                          optionLabel="label"
                          optionValue="value"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("mailLanguage"),
                          })}
                          onChange={(e) => {
                            formik.setFieldValue("mailLanguage", e.value);
                          }}
                        />
                        {getFormErrorMessage("mailLanguage")}
                      </li>
                      <li className="mt-3">
                        <label
                          htmlFor="notes"
                          className={classNames({
                            "font-semibold block mb-2": true,
                            "p-error": isFormFieldValid("notes"),
                          })}
                        >
                          {t("app.subscriptions.fields.notes")}
                        </label>
                        <InputTextarea
                          id="notes"
                          name="notes"
                          className={classNames({
                            "w-full": true,
                            "p-invalid": isFormFieldValid("notes"),
                          })}
                          value={formik.values.notes}
                          onChange={formik.handleChange}
                        />
                        {getFormErrorMessage("notes")}
                      </li>
                    </ul>
                  )}
                  {activeIndex === 0 && selectedSubscriptionId && (
                    <ul className="list-none m-0 p-0">
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentName"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentName")}
                        </label>
                        <span className="readonly-field">
                          {formik.values.studentName}
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentSurname"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentSurname")}
                        </label>
                        <span className="readonly-field">
                          {formik.values.studentSurname}
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentEmail"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentEmail")}
                        </label>
                        <span className="readonly-field">
                          {formik.values.studentEmail}
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentPhone"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentPhone")}
                        </label>
                        <span className="readonly-field">
                          {countries?.find(
                            (el: any) =>
                              el.code === formik.values.studentPrePhoneCode,
                          )?.phoneCode + formik.values.studentPhone}
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentCampusLocation"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentCampusLocation")}
                        </label>
                        <span className="readonly-field">
                          {
                            businessUnitsOptions.find(
                              (el: any) =>
                                el.value ===
                                formik.values.studentCampusLocation,
                            )?.label
                          }
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentDegreeLevel"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentDegreeLevel")}
                        </label>
                        <span className="readonly-field">
                          {
                            degreeLevelOptions.find(
                              (el: any) =>
                                el.value === formik.values.studentDegreeLevel,
                            )?.label
                          }
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentCourse"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentCourse")}
                        </label>
                        <span className="readonly-field">
                          {
                            courseCurriculumCodes.find(
                              (el: any) =>
                                el.value === formik.values.studentCourse,
                            )?.label
                          }
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="studentStartingAt"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.studentStartingAt")}
                        </label>
                        <span className="readonly-field">
                          {
                            startingAtOptions.find(
                              (el: any) =>
                                el.value === formik.values.studentStartingAt,
                            )?.label
                          }
                        </span>
                      </li>
                    </ul>
                  )}
                  {activeIndex === 1 && selectedSubscriptionId && (
                    <ul className="list-none m-0 p-0">
                      <li className="mt-3 ">
                        <label
                          htmlFor="date"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.date")}
                        </label>
                        <span className="readonly-field">
                          {DateTime.fromISO(formik.values.date).toFormat(
                            "dd/MM/yyyy",
                          )}
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="type"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.type")}
                        </label>
                        <span className="readonly-field">
                          {
                            types.find((el) => el.value === formik.values.type)
                              ?.label
                          }
                        </span>
                      </li>
                      <li className="mt-3 ">
                        <label
                          htmlFor="notes"
                          className={classNames({
                            "font-semibold block mb-2": true,
                          })}
                        >
                          {t("app.subscriptions.fields.notes")}
                        </label>
                        <span className="readonly-field">
                          {formik.values.notes}
                        </span>
                      </li>
                    </ul>
                  )}
                </form>
              </Panel>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
